"use client";

import { useState, useEffect } from "react";
import { database, auth } from "../firebase";
import { ref, onValue, off } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import ReactDOMServer from "react-dom/server";

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return "Unknown City";
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : "Unknown City";
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) {
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter((item) => item !== option && item !== options[0])
        : [...selected.filter((item) => item !== options[0]), option];

      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f8fafc",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
        maxWidth: "220px",
        marginBottom: "16px",
      }}
    >
      <h2
        style={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "8px",
          borderBottom: "2px solid #3b82f6",
          paddingBottom: "6px",
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {options.map((option) => (
          <label
            key={option}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              transition: "background-color 0.2s",
              backgroundColor: selected.includes(option) ? "#eff6ff" : "transparent",
            }}
            onMouseEnter={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: "14px",
                width: "14px",
                accentColor: "#3b82f6",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                fontSize: "14px",
                color: "#475569",
                userSelect: "none",
                fontWeight: selected.includes(option) ? "500" : "400",
              }}
            >
              {option}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Supplier Return GST Report Display Component
const SupplierReturnGSTReportDisplay = ({
  formData,
  filters,
  branchProfile,
  purchaseReturnData,
  supplierDetailsMap,
}) => {
  // Helper function to parse date
  const parseInvoiceDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split("-");
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseInvoiceDate(dateString);
    return !date || isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  // Ensure purchaseReturnData is an array
  const safeData = Array.isArray(purchaseReturnData) ? purchaseReturnData : [];

  // Filter purchaseReturnData
  let filteredPurchaseReturnData = safeData;
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter((item) => {
      const itemDate = parseInvoiceDate(item.formData?.returnDate);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  if (!filters.supplier.includes("All Suppliers")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter((item) =>
      filters.supplier.includes(item.formData?.supplier || "Unknown Supplier")
    );
  }

  if (!filters.group.includes("All Groups")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter((item) =>
      item.items?.some((i) =>
        filters.group.includes(item.groupNameMap[i.itemDescription] || "No Group Assigned")
      )
    );
  }

  if (!filters.product.includes("All Products")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter((item) =>
      item.items?.some((i) => filters.product.includes(i.itemDescription || "Unknown Product"))
    );
  }

  // Initialize displayData for Abstract report - FIXED: Corrected MRR No and Invoice No assignment
  const groupedData = filteredPurchaseReturnData.reduce((acc, returnData) => {
    const supplier = returnData.formData?.supplier || "Unknown Supplier";
    const supplierDetail = supplierDetailsMap[supplier] || {};
    const returnNo = returnData.formData?.returnNo || "N/A";

    // FIXED: Correctly assign MRR No and Invoice No
    // Use returnNo as MRR No (as per your data structure)
    const mrrNo = returnData.formData?.returnNo || "N/A";
    const invoiceNo = returnData.formData?.invoiceNo || "N/A";

    const key = `${supplier}-${returnNo}`; // Unique key to avoid overwriting
    if (!acc[key]) {
      acc[key] = {
        supplier,
        tin: supplierDetail.gstTinNo || supplierDetail.tinNo || "N/A",
        mrrNo, // This should show MRR No (from returnNo)
        mrrDate: returnData.formData?.returnDate || supplierDetail.invoiceDate || "N/A",
        invNo: invoiceNo, // This should show Invoice No
        invDate: returnData.formData?.invoiceDate || returnData.formData?.returnDate || "N/A",
        gstAmt: 0,
        grossAmt: 0,
        netValue: 0,
        totalDisc: 0,
        count: 0,
      };
    }

    // Aggregate item-level data - FIXED: Calculate GST amounts properly
    returnData.items?.forEach((item) => {
      const qty = parseFloat(item.returnQty) || 0;
      const value = parseFloat(item.value) || 0;
      const disc = parseFloat(item.disc) || 0;
      const cgstPercent = parseFloat(item.camt) || 0;
      const sgstPercent = parseFloat(item.samt) || 0;
      const cessAmt = parseFloat(item.cessAmt) || 0;

      // Calculate gross amount: value * quantity
      const grossAmount = Math.round((value * qty) * 100) / 100;
      
      // Calculate discount amount
      const discountAmount = Math.round(((grossAmount * disc) / 100) * 100) / 100;
      
      // Calculate taxable amount (gross - discount)
      const taxableAmount = Math.round((grossAmount - discountAmount) * 100) / 100;
      
      // Calculate tax amounts based on taxable amount and tax rates
      const cgstAmt = Math.round(((taxableAmount * cgstPercent) / 100) * 100) / 100;
      const sgstAmt = Math.round(((taxableAmount * sgstPercent) / 100) * 100) / 100;

      acc[key].gstAmt += Math.round((cgstAmt + sgstAmt + cessAmt) * 100) / 100;
      acc[key].grossAmt += grossAmount;
      acc[key].netValue += parseFloat(item.valueWithTax) || 0;
      acc[key].totalDisc += discountAmount;
      acc[key].count += 1;
    });

    return acc;
  }, {});

  const displayData = Object.values(groupedData);
  displayData.sort((a, b) => a.supplier.localeCompare(b.supplier));

  // Calculate totals - FIXED: Calculate totals properly based on corrected item calculations
  const totalQty = filteredPurchaseReturnData.reduce(
    (sum, returnData) =>
      sum +
      (returnData.items?.reduce((itemSum, item) => itemSum + (parseFloat(item.returnQty) || 0), 0) || 0),
    0
  );
  const totalGrossAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.grossAmt) || 0), 0);
  const totalDiscAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.totalDisc) || 0), 0);
  
  // Calculate tax amounts properly using the same logic as item-level calculations
  const totalCgstAmt = filteredPurchaseReturnData.reduce((sum, returnData) => {
    return sum + (returnData.items?.reduce((itemSum, item) => {
      const qty = parseFloat(item.returnQty) || 0;
      const value = parseFloat(item.value) || 0;
      const disc = parseFloat(item.disc) || 0;
      const cgstPercent = parseFloat(item.camt) || 0;
      
      const grossAmount = Math.round((value * qty) * 100) / 100;
      const discountAmount = Math.round(((grossAmount * disc) / 100) * 100) / 100;
      const taxableAmount = Math.round((grossAmount - discountAmount) * 100) / 100;
      const cgstAmt = Math.round(((taxableAmount * cgstPercent) / 100) * 100) / 100;
      
      return itemSum + cgstAmt;
    }, 0) || 0);
  }, 0);
  
  const totalSgstAmt = filteredPurchaseReturnData.reduce((sum, returnData) => {
    return sum + (returnData.items?.reduce((itemSum, item) => {
      const qty = parseFloat(item.returnQty) || 0;
      const value = parseFloat(item.value) || 0;
      const disc = parseFloat(item.disc) || 0;
      const sgstPercent = parseFloat(item.samt) || 0;
      
      const grossAmount = Math.round((value * qty) * 100) / 100;
      const discountAmount = Math.round(((grossAmount * disc) / 100) * 100) / 100;
      const taxableAmount = Math.round((grossAmount - discountAmount) * 100) / 100;
      const sgstAmt = Math.round(((taxableAmount * sgstPercent) / 100) * 100) / 100;
      
      return itemSum + sgstAmt;
    }, 0) || 0);
  }, 0);
  
  const totalIgstAmt = 0; // Assuming IGST is not used; adjust if needed
  const totalCessAmt = filteredPurchaseReturnData.reduce(
    (sum, returnData) =>
      sum +
      (returnData.items?.reduce((itemSum, item) => itemSum + (parseFloat(item.cessAmt) || 0), 0) || 0),
    0
  );
  const totalGstAmt = Math.round((totalCgstAmt + totalSgstAmt + totalIgstAmt) * 100) / 100;
  const totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netValue) || 0), 0);

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  const renderTableHeaders = () => (
    <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "2px solid #3b82f6" }}>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        S.No
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        Supplier Name
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        TIN
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        MRR No
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        MRR Date
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        Inv.No
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        Inv.Date
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        GST Amt
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        Gross Amount
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        Net Amount
      </th>
      <th style={{ border: "1px solid #cbd5e1", padding: "12px", textAlign: "center", fontWeight: "600", fontSize: "12px" }}>
        Total Disc
      </th>
    </tr>
  );

  const renderTableRows = () => {
    if (displayData.length === 0) {
      return (
        <tr>
          <td
            colSpan={11}
            style={{
              border: "1px solid #cbd5e1",
              padding: "30px",
              textAlign: "center",
              color: "#64748b",
              fontStyle: "italic",
              fontSize: "14px",
              backgroundColor: "#f8fafc",
            }}
          >
            No purchase return data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => (
      <tr key={`${item.supplier}-${item.invNo}-${index}`} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "center", fontSize: "11px" }}>{index + 1}</td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "left", fontSize: "11px" }}>{item.supplier}</td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "center", fontSize: "11px" }}>{item.tin}</td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "center", fontSize: "11px" }}>{item.mrrNo}</td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "center", fontSize: "11px" }}>
          {formatDisplayDate(item.mrrDate)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "center", fontSize: "11px" }}>{item.invNo}</td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "center", fontSize: "11px" }}>
          {formatDisplayDate(item.invDate)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "right", fontSize: "11px" }}>
          {(parseFloat(item.gstAmt) || 0).toFixed(2)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "right", fontSize: "11px" }}>
          {(parseFloat(item.grossAmt) || 0).toFixed(2)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "right", fontSize: "11px" }}>
          {(parseFloat(item.netValue) || 0).toFixed(2)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "8px", textAlign: "right", fontSize: "11px" }}>
          {(parseFloat(item.totalDisc) || 0).toFixed(2)}
        </td>
      </tr>
    ));
  };

  const renderTotalRow = () => {
    if (displayData.length === 0) return null;
    return (
      <tr style={{ backgroundColor: "#fef3c7", fontWeight: "bold", borderTop: "2px solid #f59e0b" }}>
        <td
          colSpan={7}
          style={{ border: "1px solid #cbd5e1", padding: "10px", textAlign: "center", fontSize: "12px", fontWeight: "600" }}
        >
          Grand Total
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600" }}>
          {totalGstAmt.toFixed(2)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600" }}>
          {totalGrossAmt.toFixed(2)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600" }}>
          {totalNetValue.toFixed(2)}
        </td>
        <td style={{ border: "1px solid #cbd5e1", padding: "10px", textAlign: "right", fontSize: "12px", fontWeight: "600" }}>
          {totalDiscAmt.toFixed(2)}
        </td>
      </tr>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        padding: "30px",
        marginTop: "20px",
        border: "2px solid #3b82f6",
        borderRadius: "12px",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        pageBreakInside: "avoid",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      }}
      className="print-only"
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          paddingBottom: "20px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              margin: "0",
              marginBottom: "15px",
              color: "#1e293b",
              textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
            }}
          >
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: "14px", lineHeight: "1.6", color: "#475569" }}>
            <div style={{ marginBottom: "8px", fontSize: "16px", fontWeight: "600", color: "#3b82f6" }}>
             SupplierwiseReturn (GSTIN)_1A 
            </div>
            <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
              <span>
                <strong>From:</strong>{" "}
                {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString("en-GB") : "N/A"}
              </span>
              <span>
                <strong>To:</strong>{" "}
                {formData.toDate ? new Date(formData.toDate).toLocaleDateString("en-GB") : "N/A"}
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            background: "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
            color: "white",
            padding: "15px 25px",
            borderRadius: "15px",
            textAlign: "center",
            minWidth: "150px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>{city}</div>
          <div style={{ fontSize: "12px", marginTop: "5px", opacity: "0.9" }}>Option: Abstract</div>
        </div>
      </div>

      {/* Purchase Return Table */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
            border: "2px solid #3b82f6",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <thead>{renderTableHeaders()}</thead>
          <tbody>{renderTableRows()}</tbody>
          <tfoot>{renderTotalRow()}</tfoot>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div
          style={{
            marginTop: "30px",
            padding: "20px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "15px", color: "#1e293b" }}>
            Summary
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "15px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total Records</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#3b82f6" }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total Quantity</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#059669" }}>{totalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total CGST</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#059669" }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total SGST</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#0891b2" }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total IGST</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#c2410c" }}>{totalIgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total CESS</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#dc2626" }}>{totalCessAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Gross Amount</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#7c3aed" }}>{totalGrossAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Net Amount</div>
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#7c3aed" }}>{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const PurchaseSupplierReturnGST = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0]);
  const [searchBy] = useState("Supplierwise");
  const [reportType] = useState("abstract");
  const [filters, setFilters] = useState({
    supplier: ["All Suppliers"],
    group: ["All Groups"],
    product: ["All Products"],
  });
  const [suppliers, setSuppliers] = useState(["All Suppliers"]);
  const [groups, setGroups] = useState(["All Groups"]);
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: "",
  });
  const [purchaseReturnData, setPurchaseReturnData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [groupsMap, setGroupsMap] = useState({});
  const [supplierDetailsMap, setSupplierDetailsMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, "_");
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    const branchProfileListener = onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`.trim(),
          phone: latestProfile.mobileNo || "",
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`),
        });
      }
    });

    // Load Suppliers and their details
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    const suppliersListener = onValue(suppliersRef, (snapshot) => {
      const suppliersData = ["All Suppliers"];
      const supplierDetails = {};

      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const supplier = childSnapshot.val();
          if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
            suppliersData.push(supplier.supplierName);
            supplierDetails[supplier.supplierName] = {
              mrnNo: supplier.mrnNo || supplier.invoiceNo || "N/A",
              invoiceDate: supplier.invoiceDate || "",
              gstTinNo: supplier.gstTinNo || supplier.tinNo || "N/A",
            };
          }
        });
      }
      setSuppliers(suppliersData);
      setSupplierDetailsMap(supplierDetails);
    });

    // Load Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    const groupsListener = onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Groups"];
      const groupsMapping = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
            groupsMapping[childSnapshot.key] = group.name;
            groupsMapping[group.name] = group.name;
          }
        });
      }
      setGroups(groupsData);
      setGroupsMap(groupsMapping);
    });

    // Load Products
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    const productsListener = onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            let groupName = "No Group Assigned";
            if (product.groupName && product.groupName !== "") {
              groupName = product.groupName;
            } else if (product.group && groupsMap[product.group]) {
              groupName = groupsMap[product.group];
            } else if (product.group && Object.values(groupsMap).includes(product.group)) {
              groupName = product.group;
            }
            groupMap[product.productName] = groupName;
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
    });

    // Load Purchase Returns
    const purchaseReturnsRef = ref(database, `users/${encodedEmail}/purchaseReturns`);
    const purchaseReturnsListener = onValue(purchaseReturnsRef, (snapshot) => {
      const data = snapshot.val();
      let returnRecords = [];
      if (data) {
        returnRecords = Object.entries(data).map(([returnId, returnData]) => ({
          returnId,
          formData: {
            returnNo: returnData.formData?.returnNo || "N/A",
            returnDate: returnData.formData?.returnDate || "",
            supplier: returnData.formData?.supplier || "Unknown Supplier",
            invoiceNo: returnData.formData?.invoiceNo || "N/A",
            invoiceDate: returnData.formData?.invoiceDate || "",
            mrnNo: returnData.formData?.mrnNo || "N/A",
          },
          items: returnData.items?.map((item) => ({
            itemDescription: item.itemDescription || "Unknown Product",
            returnQty: item.returnQty || 0,
            value: item.value || 0,
            disc: item.disc || 0,
            camt: item.camt || 0,
            samt: item.samt || 0,
            cessAmt: item.cessAmt || 0,
            valueWithTax: item.valueWithTax || 0,
            mrp: item.mrp || 0,
          })) || [],
          summary: {
            grossAmount: returnData.summary?.grossAmount || 0,
            netAmount: returnData.summary?.netAmount || 0,
            discount: returnData.summary?.discount || 0,
            igstTax: returnData.summary?.igstTax || 0,
          },
          groupNameMap: productGroupMap,
        }));
      }
      setPurchaseReturnData(returnRecords);
      setLoading(false);
    });

    return () => {
      off(branchProfileRef, "value", branchProfileListener);
      off(suppliersRef, "value", suppliersListener);
      off(groupsRef, "value", groupsListener);
      off(productsRef, "value", productsListener);
      off(purchaseReturnsRef, "value", purchaseReturnsListener);
    };
  };

  // Update purchaseReturnData when productGroupMap changes
  useEffect(() => {
    if (Object.keys(productGroupMap).length > 0 && currentUser) {
      setPurchaseReturnData((prevData) =>
        prevData.map((item) => ({
          ...item,
          groupNameMap: productGroupMap,
        }))
      );
    }
  }, [productGroupMap, currentUser]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  const handlePrint = () => {
    const printContent = (
      <SupplierReturnGSTReportDisplay
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        purchaseReturnData={purchaseReturnData}
        supplierDetailsMap={supplierDetailsMap}
      />
    );

    const htmlString = ReactDOMServer.renderToString(printContent);
    const printWindow = window.open("", "", "width=1000,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>Supplierwise Purchase Return GST Report</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; font-size: 12px; }
            @page { size: A4 landscape; margin: 10mm; }
            table { page-break-inside: auto; width: 100%; border-collapse: collapse; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; font-size: 11px; }
            th { background-color: #f1f5f9; font-weight: 600; text-align: center; }
            td { text-align: right; }
            td:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)):not(:nth-child(4)):not(:nth-child(6)) { text-align: right; }
            .supplier-name { text-align: left; max-width: 150px; word-wrap: break-word; }
            .center-column { text-align: center; }
            .total-row { background-color: #fef3c7; font-weight: bold; border-top: 2px solid #f59e0b; }
            .header-section { margin-bottom: 20px; }
            .summary-section { margin-top: 30px; page-break-inside: avoid; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType,
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <div>Loading Purchase Return Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem",
          color: "#64748b",
          fontSize: "18px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h2 style={{ color: "#1e293b", marginBottom: "16px" }}>Authentication Required</h2>
        <p>Please login to access the Supplierwise Purchase Return GST Report Generator</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "90rem",
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        padding: "32px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
        border: "1px solid #e2e8f0",
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <div
          style={{
            width: "6px",
            height: "32px",
            backgroundColor: "#3b82f6",
            borderRadius: "3px",
          }}
        ></div>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0",
          }}
        >
          SupplierwiseReturn (GSTIN)_1A
        </h1>
      </div>

      {/* Date Range and Search Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
          padding: "24px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Search By
          </label>
          <select
            value={searchBy}
       
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              
              cursor: "not-allowed",
              boxSizing: "border-box",
            }}
          >
            <option value="Supplierwise">Supplierwise</option>
          </select>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Report Type
          </label>
          <select
            value={reportType}
        
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              
              cursor: "not-allowed",
              boxSizing: "border-box",
            }}
          >
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        <FilterSection
          title="Supplier Name"
          options={suppliers}
          selected={filters.supplier}
          onChange={(value) => handleFilterChange("supplier", value)}
        />
        <FilterSection
          title="Group Name"
          options={groups}
          selected={filters.group}
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection
          title="Product Name"
          options={products}
          selected={filters.product}
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "32px",
          justifyContent: "center",
        }}
      >
        <button
          onClick={handleGenerateReport}
          style={{
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "140px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "120px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }}
        >
          Print Report
        </button>
      </div>

      {/* Report Display */}
      {showReport && (
        <SupplierReturnGSTReportDisplay
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          purchaseReturnData={purchaseReturnData}
          supplierDetailsMap={supplierDetailsMap}
        />
      )}
    </div>
  );
};

export default PurchaseSupplierReturnGST;